/**
 * Single source of truth for care app role capabilities.
 *
 * Add new roles here (plus a Firestore-rules branch and, if the role scopes
 * patient queries, a patientService branch) instead of sprinkling
 * `role === 'admin'` checks around the app.
 *
 * Roles:
 *  - superuser:      global; impersonates tenants; all pages
 *  - admin:          tenant admin; every tenant page including setup and User Management
 *  - clinical admin: sees ALL tenant patients; patient-account ops (add patients,
 *                    devices, programs), encounter edit/void, patient/pathway assignment, read-only
 *                    education; no tenant setup, no User Management, no patient
 *                    activate/deactivate, no care-alert suspend/resume
 *  - supervisor:     team patient scope; team management pages
 *  - manager:        legacy alias of supervisor's patient scope; nav has never
 *                    shown team tools for this role
 *  - care manager:   own assigned patients
 *  - viewer:         READ-ONLY; only patients matching the user's
 *                    accessClientIds / accessProviderIds filters; Patients +
 *                    Reports pages only
 *
 * The header's single "Tools" dropdown is driven by TOOLS_SECTIONS below: each
 * link names the capability that reveals it, so a role's menu and its route
 * guard are derived from the same data and cannot drift apart.
 */

const BASE = {
  label: '',
  // Patient scope
  viewAllPatients: false,
  scopedByAccessFilters: false, // viewer: patient queries filtered by accessClientIds/accessProviderIds
  readOnly: false,
  // Page capabilities
  canManageUsers: false,
  canManagePathways: false, // Pathway Builder (author pathway content)
  canAssignPathways: false, // Pathway Assignment (bulk-assign existing pathways)
  canManagePatientAssignments: false,
  canUseGroupMessaging: false,
  canSeeAdminTools: false,
  canSeeSuperuserTools: false,
  /** Admin Dashboard: program KPIs + per-user productivity (admin/superuser only). */
  canSeeAdminDashboard: false,
  /** Tenant setup: billing catalog, reason lists, care alert settings, phone numbers. */
  canSeeBillingSetup: false,
  canSeeTenantAdminSettings: false,
  /** Education Library: see the list vs. add/edit/delete entries. */
  canSeeEducationLibrary: false,
  canManageEducationLibrary: false,
  /** RPM device tooling: shared operational pages vs. admin-only surfaces. */
  canSeeRpmDeviceTools: false,
  canSeeRpmReadingCharts: false,
  canSeeRpmSerialBackfill: false,
  /** Write actions on the admin RPM surfaces (backfill serials, dismiss pairing help). */
  canRunSerialBackfill: false,
  canTriagePairingHelpQueue: false,
  /** Patient-account actions (see role overrides). */
  canCreatePatients: false,
  /** "Create anyway" when Add Patient finds a name + DOB match; others only see the matches. */
  canOverrideDuplicatePatients: false,
  canManagePatientDevices: false,
  canManagePatientPrograms: false,
  canEditCareManagerAssignments: false,
  canEditPatientPhoto: false,
  /** Move an orphaned reading onto one of that patient's own assigned devices. */
  canReassignReadings: false,
  /** Void / restore readings and see voided readings on the patient Readings tab. */
  canVoidReadings: false,
  /** De-activate patient, suspend/resume care alerts (admin/superuser only). */
  canManagePatientLifecycle: false,
  /** Monthly Billing Review page: record Bill / No Bill decisions per patient-period. */
  canManageBillingReview: false,
  /** Encounter log: edit and void encounters, view and export voided ones. */
  canManageEncounters: false,
  /** Helpdesk: page access, the open-ticket badge, and the Help Tools tab. */
  canSeeHelpdesk: false,
  canSeeHelpdeskTicketBadge: false,
  canUseHelpdeskTools: false,
  /** System Updates log: everyone with Helpdesk reads it; admins author entries. */
  canManageSystemUpdates: false,
  /** Bonus programs: settings/assignments/overrides (admin tier) vs. own scorecard. */
  canManageBonusPrograms: false,
  canSeeMyBonus: false,
  /** Client KPI Report under Reports (every role except viewer). */
  canSeeKpiReport: false,
  /** Internal PRO Detail report (patient names + comments). */
  canSeeProInternalReport: false,
  // Top-level nav items outside the Tools dropdown
  nav: {
    dashboard: true,
    careQueue: true,
    patients: true,
    worklist: true,
    reports: true,
    superuser: false,
  },
  /**
   * Route guard. When `restrictRoutes` is false the role may reach any route.
   * When true, allowed prefixes are derived from the role's nav flags plus every
   * Tools link its capabilities reveal, and `extraAllowedPathPrefixes` covers
   * pages reached from cards or links rather than from the menu.
   */
  restrictRoutes: false,
  extraAllowedPathPrefixes: [],
  defaultRoute: '/',
};

/**
 * The header "Tools" dropdown, as data. Sections and links render only when the
 * signed-in role holds the named capability; a section with no visible links is
 * dropped, and the trigger hides when no section survives.
 */
export const TOOLS_SECTIONS = [
  {
    id: 'rpmDevices',
    label: 'RPM Devices',
    column: 0,
    links: [
      { to: '/rpm/devices/assignments', label: 'Patient Devices', capability: 'canSeeRpmDeviceTools' },
      { to: '/rpm/devices/setup', label: 'Device Setup', capability: 'canSeeRpmDeviceTools' },
      { to: '/rpm/devices/war-room', label: 'Device War Room', capability: 'canSeeRpmDeviceTools' },
      { to: '/rpm/devices/help-queue', label: 'Pairing help queue', capability: 'canSeeRpmDeviceTools' },
      { to: '/alerts/reading-quarantine', label: 'Reading quarantine', capability: 'canSeeRpmDeviceTools' },
      { to: '/alerts/unassigned-devices', label: 'Unassigned readings', capability: 'canSeeRpmDeviceTools' },
      { to: '/alerts/parameter-mismatch', label: 'Parameter mismatch', capability: 'canSeeRpmDeviceTools' },
      { to: '/rpm/unsynced-readings', label: 'Unsynced readings', capability: 'canSeeRpmDeviceTools' },
      { to: '/rpm/devices/reading-charts', label: 'Reading charts', capability: 'canSeeRpmReadingCharts' },
      { to: '/rpm/devices/sn-backfilling', label: 'Missing Serial Numbers', capability: 'canSeeRpmSerialBackfill' },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    column: 0,
    links: [
      { to: '/users', label: 'User Management', capability: 'canManageUsers' },
      { to: '/patient-assignments', label: 'Patient Assignments', capability: 'canManagePatientAssignments' },
      { to: '/pathways', label: 'Pathway Builder', capability: 'canManagePathways' },
      { to: '/bulk-pathways', label: 'Pathway Assignment', capability: 'canAssignPathways' },
      { to: '/bulk-messaging', label: 'Group Messaging', capability: 'canUseGroupMessaging' },
      { to: '/admin/bonus-settings', label: 'Bonus Settings', capability: 'canManageBonusPrograms' },
    ],
  },
  {
    id: 'billing',
    label: 'Billing Setup',
    column: 1,
    links: [
      { to: '/billing-setup/clients', label: 'Clients', capability: 'canSeeBillingSetup' },
      { to: '/billing-setup/client-sites', label: 'Client Sites', capability: 'canSeeBillingSetup' },
      { to: '/billing-setup/providers', label: 'Providers', capability: 'canSeeBillingSetup' },
      { to: '/billing-setup/cpt-codes', label: 'CPT Codes', capability: 'canSeeBillingSetup' },
      { to: '/billing-setup/billing-rules', label: 'Billing Rules', capability: 'canSeeBillingSetup' },
      { to: '/billing-setup/diagnosis-codes', label: 'Diagnosis Codes', capability: 'canSeeBillingSetup' },
      { to: '/insurances', label: 'Insurance', capability: 'canSeeBillingSetup' },
    ],
  },
  {
    id: 'metricDefaults',
    label: 'Metric Defaults',
    column: 1,
    links: [
      {
        to: '/billing-setup/session-minutes-defaults',
        label: 'Encounter Session Minutes',
        capability: 'canSeeBillingSetup',
      },
    ],
  },
  {
    id: 'patientAdmin',
    label: 'Patients',
    column: 2,
    links: [
      { to: '/admin/phone-numbers', label: 'Tenant phone numbers', capability: 'canSeeTenantAdminSettings' },
      { to: '/admin/reasons', label: 'Reasons', capability: 'canSeeTenantAdminSettings' },
      { to: '/admin/resolve-notes', label: 'Resolve Notes', capability: 'canSeeTenantAdminSettings' },
      { to: '/admin/care-alert-settings', label: 'Care Alert Settings', capability: 'canSeeTenantAdminSettings' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    column: 2,
    links: [
      { to: '/admin/report-settings', label: 'Report Settings', capability: 'canSeeTenantAdminSettings' },
    ],
  },
  {
    id: 'education',
    label: 'Education',
    column: 2,
    links: [
      { to: '/education-library', label: 'Education Library', capability: 'canSeeEducationLibrary' },
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    column: 2,
    links: [
      { to: '/helpdesk', label: 'Helpdesk', capability: 'canSeeHelpdesk', badge: 'openTickets' },
    ],
  },
];

/** Number of columns the Tools mega-menu lays sections out across. */
export const TOOLS_COLUMN_COUNT = 3;

const ROLE_PERMISSIONS = {
  superuser: {
    ...BASE,
    label: 'Superuser',
    viewAllPatients: true,
    canManageUsers: true,
    canManagePathways: true,
    canAssignPathways: true,
    canManagePatientAssignments: true,
    canUseGroupMessaging: true,
    canSeeAdminTools: true,
    canSeeSuperuserTools: true,
    canSeeAdminDashboard: true,
    canSeeBillingSetup: true,
    canSeeTenantAdminSettings: true,
    canSeeEducationLibrary: true,
    canManageEducationLibrary: true,
    canSeeRpmDeviceTools: true,
    canSeeRpmReadingCharts: true,
    canSeeRpmSerialBackfill: true,
    canRunSerialBackfill: true,
    canTriagePairingHelpQueue: true,
    canCreatePatients: true,
    canOverrideDuplicatePatients: true,
    canManagePatientDevices: true,
    canManagePatientPrograms: true,
    canEditCareManagerAssignments: true,
    canEditPatientPhoto: true,
    canReassignReadings: true,
    canVoidReadings: true,
    canManagePatientLifecycle: true,
    canManageBillingReview: true,
    canManageEncounters: true,
    canSeeHelpdesk: true,
    canSeeHelpdeskTicketBadge: true,
    canUseHelpdeskTools: true,
    canManageSystemUpdates: true,
    canManageBonusPrograms: true,
    canSeeMyBonus: true,
    canSeeKpiReport: true,
    canSeeProInternalReport: true,
    nav: { ...BASE.nav, superuser: true },
  },
  admin: {
    ...BASE,
    label: 'Admin',
    viewAllPatients: true,
    canManageUsers: true,
    canManagePathways: true,
    canAssignPathways: true,
    canManagePatientAssignments: true,
    canUseGroupMessaging: true,
    canSeeAdminTools: true,
    canSeeAdminDashboard: true,
    canSeeBillingSetup: true,
    canSeeTenantAdminSettings: true,
    canSeeEducationLibrary: true,
    canManageEducationLibrary: true,
    canSeeRpmDeviceTools: true,
    canSeeRpmReadingCharts: true,
    canSeeRpmSerialBackfill: true,
    canRunSerialBackfill: true,
    canTriagePairingHelpQueue: true,
    canCreatePatients: true,
    canOverrideDuplicatePatients: true,
    canManagePatientDevices: true,
    canManagePatientPrograms: true,
    canEditCareManagerAssignments: true,
    canEditPatientPhoto: true,
    canReassignReadings: true,
    canVoidReadings: true,
    canManagePatientLifecycle: true,
    canManageBillingReview: true,
    canManageEncounters: true,
    canSeeHelpdesk: true,
    canSeeHelpdeskTicketBadge: true,
    canUseHelpdeskTools: true,
    canManageSystemUpdates: true,
    canManageBonusPrograms: true,
    canSeeMyBonus: true,
    canSeeKpiReport: true,
    canSeeProInternalReport: true,
  },
  'clinical admin': {
    ...BASE,
    label: 'Clinical admin',
    viewAllPatients: true,
    canCreatePatients: true,
    canManagePatientDevices: true,
    canManagePatientPrograms: true,
    canEditCareManagerAssignments: true,
    canEditPatientPhoto: true,
    canReassignReadings: true,
    canVoidReadings: true,
    canManagePatientAssignments: true,
    canAssignPathways: true,
    canManageBillingReview: true,
    canManageEncounters: true,
    // Read-only: useful when reviewing what education a patient's pathway assigns.
    canSeeEducationLibrary: true,
    // Sees the shared device pages and can view the serial backfill queue, but
    // the backfill itself and pairing-queue triage stay admin-only.
    canSeeRpmDeviceTools: true,
    canSeeRpmSerialBackfill: true,
    canSeeHelpdesk: true,
    canUseHelpdeskTools: true,
    canSeeMyBonus: true,
    canSeeKpiReport: true,
    canSeeProInternalReport: true,
    restrictRoutes: true,
    // Reached from RPM Dashboard "Readings Issues" cards rather than the menu.
    extraAllowedPathPrefixes: [
      '/alerts/unassigned-devices',
      '/alerts/parameter-mismatch',
      '/rpm/unsynced-readings',
    ],
  },
  supervisor: {
    ...BASE,
    label: 'Supervisor',
    canManageUsers: true,
    canManagePathways: true,
    canAssignPathways: true,
    canManagePatientAssignments: true,
    canUseGroupMessaging: true,
    canCreatePatients: true,
    canOverrideDuplicatePatients: true,
    canManagePatientDevices: true,
    canManagePatientPrograms: true,
    canEditCareManagerAssignments: true,
    canEditPatientPhoto: true,
    canReassignReadings: true,
    canSeeRpmDeviceTools: true,
    canSeeHelpdesk: true,
    canUseHelpdeskTools: true,
    canSeeMyBonus: true,
    canSeeKpiReport: true,
    canSeeProInternalReport: true,
  },
  // Legacy alias: supervisor-like patient scope (queries/rules), but the nav
  // has never shown team tools for this role — keep behavior unchanged.
  manager: {
    ...BASE,
    label: 'Manager (legacy)',
    canSeeRpmDeviceTools: true,
    canSeeHelpdesk: true,
    canSeeMyBonus: true,
    canSeeKpiReport: true,
  },
  'care manager': {
    ...BASE,
    label: 'Care manager',
    canReassignReadings: true,
    canSeeRpmDeviceTools: true,
    canSeeHelpdesk: true,
    canSeeMyBonus: true,
    canSeeKpiReport: true,
  },
  viewer: {
    ...BASE,
    label: 'Viewer',
    scopedByAccessFilters: true,
    readOnly: true,
    nav: {
      ...BASE.nav,
      dashboard: false,
      careQueue: false,
      worklist: false,
    },
    restrictRoutes: true,
    defaultRoute: '/patients',
  },
};

const UNKNOWN_ROLE = {
  ...BASE,
  label: 'Unknown',
  nav: { ...BASE.nav },
};

export function normalizeRole(role) {
  return String(role || '').toLowerCase().trim();
}

/**
 * Sections of the Tools menu this capability set can see, with links the role
 * cannot reach removed and empty sections dropped.
 */
export function getToolsSections(permissions) {
  if (!permissions) return [];
  return TOOLS_SECTIONS
    .map((section) => ({
      ...section,
      links: section.links.filter((link) => permissions[link.capability] === true),
    }))
    .filter((section) => section.links.length > 0);
}

/** Every Tools path this capability set can reach. */
function toolsPathsFor(permissions) {
  const paths = [];
  for (const section of TOOLS_SECTIONS) {
    for (const link of section.links) {
      if (permissions[link.capability] === true) paths.push(link.to);
    }
  }
  return paths;
}

/**
 * Allowed route prefixes for a restricted role, derived from the same data that
 * builds its menu so a granted page is always reachable.
 */
function derivedAllowedPathPrefixes(permissions) {
  const paths = new Set();
  if (permissions.nav.dashboard) paths.add('/');
  if (permissions.nav.careQueue) paths.add('/care-queue');
  if (permissions.nav.patients) paths.add('/patients');
  if (permissions.nav.worklist) paths.add('/worklist');
  if (permissions.nav.reports) paths.add('/reports');
  if (permissions.canSeeAdminDashboard) paths.add('/admin-dashboard');
  if (permissions.canSeeMyBonus) paths.add('/my-bonus');
  toolsPathsFor(permissions).forEach((p) => paths.add(p));
  (permissions.extraAllowedPathPrefixes || []).forEach((p) => paths.add(p));
  return [...paths];
}

/** Capability map for a role (unknown roles get base capabilities: patient pages only, no scope). */
export function getRolePermissions(role) {
  return ROLE_PERMISSIONS[normalizeRole(role)] || UNKNOWN_ROLE;
}

/** Roles assignable from the User Management UI (superuser stays console-managed). */
export const ASSIGNABLE_ROLE_OPTIONS = [
  { value: 'care manager', label: 'Care manager' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'clinical admin', label: 'Clinical admin (all patients, patient ops, no setup)' },
  { value: 'admin', label: 'Admin' },
  { value: 'viewer', label: 'Viewer (read-only, by client or provider)' },
];

/**
 * True when the pathname is allowed for the role. Prefix '/' only matches the
 * exact root path so restricted roles don't accidentally match everything.
 */
export function isPathAllowedForRole(role, pathname) {
  const permissions = getRolePermissions(role);
  if (!permissions.restrictRoutes) return true;
  const prefixes = derivedAllowedPathPrefixes(permissions);
  const path = pathname || '/';
  return prefixes.some((prefix) => {
    if (prefix === '/') return path === '/';
    return path === prefix || path.startsWith(prefix + '/');
  });
}

/** Exposed for tests and diagnostics: the resolved prefix list for a role. */
export function getAllowedPathPrefixesForRole(role) {
  const permissions = getRolePermissions(role);
  return permissions.restrictRoutes ? derivedAllowedPathPrefixes(permissions) : null;
}

export function getDefaultRouteForRole(role) {
  return getRolePermissions(role).defaultRoute || '/';
}
